import React, { useState, useEffect } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  VStack,
  HStack,
  FormControl,
  FormLabel,
  FormErrorMessage,
  FormHelperText,
  Input,
  Textarea,
  Select,
  Switch,
  Box,
  Text,
  Badge,
  IconButton,
  useToast,
  SimpleGrid,
  Alert,
  AlertIcon,
  Stepper,
  Step,
  StepIndicator,
  StepStatus,
  StepIcon,
  StepNumber,
  StepTitle,
  StepSeparator,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
  useSteps,
} from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';

// Используем правильный тип API
interface CreateTeamRequest {
  team?: {
    name: string;
    description?: string;
    team_type: string;
    status: string;
    metadata?: { [key: string]: string };
    contacts?: { [key: string]: string };
  };
  security_settings?: {
    require_invitation: boolean;
    allow_self_join: boolean;
    require_admin_approval: boolean;
    max_members: number;
    enable_audit_logging: boolean;
  };
  ui_settings?: {
    language: string;
    timezone: string;
    date_format: string;
    time_format: string;
  };
  user_settings?: {
    default_role_name: string;
    auto_approve_members: boolean;
    allow_member_invitations: boolean;
  };
  notification_settings?: {
    notifications_enabled: boolean;
    email_enabled: boolean;
    push_enabled: boolean;
  };
}

interface TeamContact {
  type: string;
  value: string;
  label?: string;
}

interface TeamFormData {
  name: string;
  description: string;
  team_type: string;
  status: string;
  metadata: { [key: string]: string };
  contacts: TeamContact[];
  security_settings: {
    require_invitation: boolean;
    allow_self_join: boolean;
    require_admin_approval: boolean;
    max_members: number;
    enable_audit_logging: boolean;
  };
  ui_settings: {
    language: string;
    timezone: string;
    date_format: string;
    time_format: string;
  };
  user_settings: {
    default_role_name: string;
    auto_approve_members: boolean;
    allow_member_invitations: boolean;
  };
  notification_settings: {
    notifications_enabled: boolean;
    email_enabled: boolean;
    push_enabled: boolean;
  };
}

interface ValidationErrors {
  name?: string;
  description?: string;
  team_type?: string;
  status?: string;
  contacts?: string[];
  metadata?: string;
  settings?: string;
}

interface CreateTeamWizardProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: CreateTeamRequest) => Promise<void>;
  isSubmitting?: boolean;
}

const getContactTypes = (t: any) => [
  { value: 'email', label: t('common.email') },
  { value: 'phone', label: t('profile.phone') },
  { value: 'website', label: t('teams.contactWebsite') },
  { value: 'slack', label: 'Slack' },
  { value: 'discord', label: 'Discord' },
  { value: 'telegram', label: 'Telegram' },
  { value: 'other', label: t('teams.contactOther') },
];

const steps = [
  { title: 'Basic Info', description: 'Team details' },
  { title: 'Security', description: 'Access control' },
  { title: 'UI Settings', description: 'Appearance' },
  { title: 'Users', description: 'Member settings' },
  { title: 'Notifications', description: 'Communication' },
];

export const CreateTeamWizard: React.FC<CreateTeamWizardProps> = ({
  isOpen,
  onClose,
  onSubmit,
  isSubmitting = false,
}) => {
  const { t } = useTranslation();
  const toast = useToast();
  const contactTypes = getContactTypes(t);
  const { activeStep, setActiveStep } = useSteps({
    index: 0,
    count: steps.length,
  });
  
  // Color mode values
  const modalBg = 'bg.surface';
  const cardBg = 'bg.surface';
  const surfaceBg = 'bg.subtle';
  const inputBg = 'bg.surface';
  const headingColor = 'text.primary';
  const textColor = 'text.secondary';
  const mutedTextColor = 'text.muted';
  const borderColor = 'border.default';
  const primaryColorScheme = 'primary';
  const focusRing = { borderColor: 'primary.500', boxShadow: '0 0 0 1px' };

  // Form state
  const [formData, setFormData] = useState<TeamFormData>({
    name: '',
    description: '',
    team_type: 'project',
    status: 'active',
    metadata: {},
    contacts: [],
    security_settings: {
      require_invitation: true,
      allow_self_join: false,
      require_admin_approval: true,
      max_members: 100,
      enable_audit_logging: true,
    },
    ui_settings: {
      language: 'en',
      timezone: 'UTC',
      date_format: 'DD/MM/YYYY',
      time_format: '24h',
    },
    user_settings: {
      default_role_name: 'member',
      auto_approve_members: false,
      allow_member_invitations: true,
    },
    notification_settings: {
      notifications_enabled: true,
      email_enabled: true,
      push_enabled: true,
    },
  });

  const [errors, setErrors] = useState<ValidationErrors>({});
  const [newContact, setNewContact] = useState<TeamContact>({
    type: 'email',
    value: '',
    label: '',
  });

  // Reset form when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      setFormData({
        name: '',
        description: '',
        team_type: 'project',
        status: 'active',
        metadata: {},
        contacts: [],
        security_settings: {
          require_invitation: true,
          allow_self_join: false,
          require_admin_approval: true,
          max_members: 100,
          enable_audit_logging: true,
        },
        ui_settings: {
          language: 'en',
          timezone: 'UTC',
          date_format: 'DD/MM/YYYY',
          time_format: '24h',
        },
        user_settings: {
          default_role_name: 'member',
          auto_approve_members: false,
          allow_member_invitations: true,
        },
        notification_settings: {
          notifications_enabled: true,
          email_enabled: true,
          push_enabled: true,
        },
      });
      setErrors({});
      setActiveStep(0);
      setNewContact({ type: 'email', value: '', label: '' });
    }
  }, [isOpen, setActiveStep]);

  // Validation
  const validateCurrentStep = (): boolean => {
    const newErrors: ValidationErrors = {};

    if (activeStep === 0) {
      // Basic Info validation
      if (!formData.name.trim()) {
        newErrors.name = t('teams.validation.nameRequired');
      } else if (formData.name.length < 2) {
        newErrors.name = t('teams.validation.nameTooShort');
      } else if (formData.name.length > 100) {
        newErrors.name = t('teams.validation.nameTooLong');
      }

      if (formData.description && formData.description.length > 500) {
        newErrors.description = t('teams.validation.descriptionTooLong');
      }
    }

    // Contact validation for any step
    const contactErrors: string[] = [];
    formData.contacts.forEach((contact, index) => {
      if (contact.type === 'email' && contact.value && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(contact.value)) {
        contactErrors[index] = t('teams.validation.invalidEmail');
      }
      if (contact.type === 'phone' && contact.value && !/^\+?[\d\s\-\(\)]+$/.test(contact.value)) {
        contactErrors[index] = t('teams.validation.invalidPhone');
      }
      if (contact.type === 'website' && contact.value && !/^https?:\/\/.+/.test(contact.value)) {
        contactErrors[index] = t('teams.validation.invalidWebsite');
      }
    });

    if (contactErrors.length > 0) {
      newErrors.contacts = contactErrors;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validateCurrentStep()) {
      setActiveStep(activeStep + 1);
    }
  };

  const handleBack = () => {
    setActiveStep(activeStep - 1);
  };

  const handleSubmit = async () => {
    if (!validateCurrentStep()) {
      toast({
        title: t('teams.validation.formHasErrors'),
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    try {
      const submitData: CreateTeamRequest = {
        team: {
          name: formData.name.trim(),
          description: formData.description.trim() || undefined,
          team_type: formData.team_type,
          status: formData.status,
          metadata: Object.keys(formData.metadata).length > 0 ? formData.metadata : undefined,
          contacts: formData.contacts.length > 0 ? formData.contacts.reduce((acc, contact) => {
            if (contact.value.trim()) {
              acc[contact.type] = contact.value.trim();
            }
            return acc;
          }, {} as { [key: string]: string }) : undefined,
        },
        security_settings: formData.security_settings,
        ui_settings: formData.ui_settings,
        user_settings: formData.user_settings,
        notification_settings: formData.notification_settings,
      };

      await onSubmit(submitData);
      onClose();
      
      toast({
        title: t('teams.createSuccess'),
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      console.error('Create team error:', error);
      toast({
        title: t('teams.createError'),
        description: error instanceof Error ? error.message : t('common.error'),
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const handleInputChange = (field: keyof TeamFormData, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear related errors
    const fieldError = field as keyof ValidationErrors;
    if (errors[fieldError]) {
      setErrors(prev => ({ ...prev, [fieldError]: undefined }));
    }
  };

  const addContact = () => {
    if (newContact.value.trim()) {
      setFormData(prev => ({
        ...prev,
        contacts: [...prev.contacts, { ...newContact, value: newContact.value.trim() }],
      }));
      setNewContact({ type: 'email', value: '', label: '' });
    }
  };

  const removeContact = (index: number) => {
    setFormData(prev => ({
      ...prev,
      contacts: prev.contacts.filter((_, i) => i !== index),
    }));
  };

  const renderStepContent = () => {
    switch (activeStep) {
      case 0:
        return (
          <VStack spacing={6} align="stretch">
            <Alert status="info" borderRadius="md" variant="subtle">
              <AlertIcon />
              <Text fontSize="sm" color={textColor}>Fill in basic information about your team</Text>
            </Alert>

            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
              <FormControl isRequired isInvalid={!!errors.name}>
                <FormLabel color={headingColor}>{t('teams.teamName')}</FormLabel>
                <Input
                  value={formData.name}
                  onChange={(e) => handleInputChange('name', e.target.value)}
                  placeholder={t('teams.enterTeamName')}
                  bg={inputBg}
                  borderColor={borderColor}
                  _focus={focusRing}
                />
                <FormErrorMessage>{errors.name}</FormErrorMessage>
                <FormHelperText color={mutedTextColor}>{t('teams.teamNameHelp')}</FormHelperText>
              </FormControl>

              <FormControl>
                <FormLabel color={headingColor}>{t('teams.teamType')}</FormLabel>
                <Select
                  value={formData.team_type}
                  onChange={(e) => handleInputChange('team_type', e.target.value)}
                  bg={inputBg}
                  borderColor={borderColor}
                  _focus={focusRing}
                >
                  <option value="project">{t('teams.teamTypeProject')}</option>
                  <option value="business">{t('teams.teamTypeBusiness')}</option>
                  <option value="social">{t('teams.teamTypeSocial')}</option>
                  <option value="gaming">{t('teams.teamTypeGaming')}</option>
                  <option value="educational">{t('teams.teamTypeEducational')}</option>
                  <option value="nonprofit">{t('teams.teamTypeNonprofit')}</option>
                </Select>
              </FormControl>
            </SimpleGrid>

            <FormControl isInvalid={!!errors.description}>
              <FormLabel color={headingColor}>{t('teams.description')}</FormLabel>
              <Textarea
                value={formData.description}
                onChange={(e) => handleInputChange('description', e.target.value)}
                placeholder={t('teams.enterTeamDescription')}
                rows={4}
                resize="vertical"
                bg={inputBg}
                borderColor={borderColor}
                _focus={focusRing}
              />
              <FormErrorMessage>{errors.description}</FormErrorMessage>
              <FormHelperText color={mutedTextColor}>{t('teams.descriptionHelp')}</FormHelperText>
            </FormControl>

            <FormControl>
              <FormLabel color={headingColor}>{t('teams.status')}</FormLabel>
              <Select
                value={formData.status}
                onChange={(e) => handleInputChange('status', e.target.value)}
                bg={inputBg}
                borderColor={borderColor}
                _focus={focusRing}
              >
                <option value="active">{t('teams.statusActive')}</option>
                <option value="inactive">{t('teams.statusInactive')}</option>
                <option value="draft">{t('teams.statusDraft')}</option>
              </Select>
            </FormControl>

            {/* Add contact form */}
            <Box bg={surfaceBg} p={4} borderRadius="md" border="1px" borderColor={borderColor}>
              <Text fontWeight="semibold" mb={3} color={headingColor}>{t('teams.addContact')} ({t('common.optional')})</Text>
              <VStack spacing={3} align="stretch">
                <SimpleGrid columns={{ base: 1, md: 3 }} spacing={3}>
                  <FormControl>
                    <FormLabel fontSize="sm" color={headingColor}>{t('common.contactType')}</FormLabel>
                    <Select
                      value={newContact.type}
                      onChange={(e) => setNewContact(prev => ({ ...prev, type: e.target.value }))}
                      size="sm"
                      bg={inputBg}
                      borderColor={borderColor}
                    >
                      {contactTypes.map(type => (
                        <option key={type.value} value={type.value}>
                          {type.label}
                        </option>
                      ))}
                    </Select>
                  </FormControl>

                  <FormControl>
                    <FormLabel fontSize="sm" color={headingColor}>{t('common.contactValue')}</FormLabel>
                    <Input
                      value={newContact.value}
                      onChange={(e) => setNewContact(prev => ({ ...prev, value: e.target.value }))}
                      placeholder={t('common.enterContactValue')}
                      size="sm"
                      bg={inputBg}
                      borderColor={borderColor}
                    />
                  </FormControl>

                  <FormControl>
                    <FormLabel fontSize="sm" color={headingColor}>{t('teams.contactLabel')} ({t('common.optional')})</FormLabel>
                    <Input
                      value={newContact.label || ''}
                      onChange={(e) => setNewContact(prev => ({ ...prev, label: e.target.value }))}
                      placeholder={t('common.enterContactLabel')}
                      size="sm"
                      bg={inputBg}
                      borderColor={borderColor}
                    />
                  </FormControl>
                </SimpleGrid>

                <HStack>
                  <Button
                    onClick={addContact}
                    isDisabled={!newContact.value.trim()}
                    colorScheme={primaryColorScheme}
                    variant="outline"
                    size="sm"
                  >
                    {t('teams.addContact')}
                  </Button>
                </HStack>
              </VStack>
            </Box>

            {/* Current contacts */}
            {formData.contacts.length > 0 && (
              <Box>
                <Text fontWeight="semibold" mb={3} color={headingColor}>{t('common.currentContacts')}</Text>
                <VStack spacing={2} align="stretch">
                  {formData.contacts.map((contact, index) => (
                    <HStack
                      key={index}
                      p={3}
                      bg={cardBg}
                      borderRadius="md"
                      border="1px"
                      borderColor={borderColor}
                      justifyContent="space-between"
                    >
                      <HStack spacing={3}>
                        <Badge colorScheme={primaryColorScheme} variant="subtle">
                          {contactTypes.find(ct => ct.value === contact.type)?.label || contact.type}
                        </Badge>
                        <Text fontSize="sm" color={textColor}>
                          {contact.label ? `${contact.label}: ${contact.value}` : contact.value}
                        </Text>
                      </HStack>
                      <IconButton
                        aria-label={t('common.delete')}
                        icon={<span>×</span>}
                        onClick={() => removeContact(index)}
                        size="sm"
                        variant="ghost"
                        colorScheme="red"
                      />
                    </HStack>
                  ))}
                </VStack>
              </Box>
            )}
          </VStack>
        );

      case 1:
        return (
          <VStack spacing={6} align="stretch">
            <Alert status="info" borderRadius="md" variant="subtle">
              <AlertIcon />
              <Text fontSize="sm" color={textColor}>Configure security and access control settings</Text>
            </Alert>

            <Box bg={surfaceBg} p={4} borderRadius="md" border="1px" borderColor={borderColor}>
              <Text fontWeight="semibold" mb={3} color={headingColor}>Access Control</Text>
              <VStack spacing={4} align="stretch">
                <HStack justifyContent="space-between">
                  <VStack align="start" spacing={1}>
                    <Text fontSize="sm" fontWeight="medium" color={headingColor}>Require invitation</Text>
                    <Text fontSize="xs" color={mutedTextColor}>Users must be invited to join</Text>
                  </VStack>
                  <Switch
                    isChecked={formData.security_settings.require_invitation}
                    onChange={(e) => setFormData(prev => ({ 
                      ...prev, 
                      security_settings: { ...prev.security_settings, require_invitation: e.target.checked } 
                    }))}
                    colorScheme={primaryColorScheme}
                  />
                </HStack>

                <HStack justifyContent="space-between">
                  <VStack align="start" spacing={1}>
                    <Text fontSize="sm" fontWeight="medium" color={headingColor}>Allow self-join</Text>
                    <Text fontSize="xs" color={mutedTextColor}>Users can join without invitation</Text>
                  </VStack>
                  <Switch
                    isChecked={formData.security_settings.allow_self_join}
                    onChange={(e) => setFormData(prev => ({ 
                      ...prev, 
                      security_settings: { ...prev.security_settings, allow_self_join: e.target.checked } 
                    }))}
                    colorScheme={primaryColorScheme}
                  />
                </HStack>

                <HStack justifyContent="space-between">
                  <VStack align="start" spacing={1}>
                    <Text fontSize="sm" fontWeight="medium" color={headingColor}>Require admin approval</Text>
                    <Text fontSize="xs" color={mutedTextColor}>New members need admin approval</Text>
                  </VStack>
                  <Switch
                    isChecked={formData.security_settings.require_admin_approval}
                    onChange={(e) => setFormData(prev => ({ 
                      ...prev, 
                      security_settings: { ...prev.security_settings, require_admin_approval: e.target.checked } 
                    }))}
                    colorScheme={primaryColorScheme}
                  />
                </HStack>

                <HStack justifyContent="space-between">
                  <VStack align="start" spacing={1}>
                    <Text fontSize="sm" fontWeight="medium" color={headingColor}>Enable audit logging</Text>
                    <Text fontSize="xs" color={mutedTextColor}>Track all team activities</Text>
                  </VStack>
                  <Switch
                    isChecked={formData.security_settings.enable_audit_logging}
                    onChange={(e) => setFormData(prev => ({ 
                      ...prev, 
                      security_settings: { ...prev.security_settings, enable_audit_logging: e.target.checked } 
                    }))}
                    colorScheme={primaryColorScheme}
                  />
                </HStack>
              </VStack>
            </Box>

            <Box bg={surfaceBg} p={4} borderRadius="md" border="1px" borderColor={borderColor}>
              <Text fontWeight="semibold" mb={3} color={headingColor}>Limits</Text>
              <FormControl>
                <FormLabel fontSize="sm" color={headingColor}>Maximum members</FormLabel>
                <NumberInput
                  value={formData.security_settings.max_members}
                  onChange={(_, valueAsNumber) => setFormData(prev => ({ 
                    ...prev, 
                    security_settings: { ...prev.security_settings, max_members: valueAsNumber || 0 } 
                  }))}
                  min={0}
                  max={10000}
                  bg={inputBg}
                >
                  <NumberInputField />
                  <NumberInputStepper>
                    <NumberIncrementStepper />
                    <NumberDecrementStepper />
                  </NumberInputStepper>
                </NumberInput>
                <FormHelperText color={mutedTextColor}>Set to 0 for unlimited</FormHelperText>
              </FormControl>
            </Box>
          </VStack>
        );

      case 2:
        return (
          <VStack spacing={6} align="stretch">
            <Alert status="info" borderRadius="md" variant="subtle">
              <AlertIcon />
              <Text fontSize="sm" color={textColor}>Configure UI preferences and localization</Text>
            </Alert>

            <Box bg={surfaceBg} p={4} borderRadius="md" border="1px" borderColor={borderColor}>
              <Text fontWeight="semibold" mb={3} color={headingColor}>Localization</Text>
              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                <FormControl>
                  <FormLabel fontSize="sm" color={headingColor}>Language</FormLabel>
                  <Select
                    value={formData.ui_settings.language}
                    onChange={(e) => setFormData(prev => ({ 
                      ...prev, 
                      ui_settings: { ...prev.ui_settings, language: e.target.value } 
                    }))}
                    bg={inputBg}
                    borderColor={borderColor}
                  >
                    <option value="en">English</option>
                    <option value="es">Español</option>
                    <option value="fr">Français</option>
                    <option value="de">Deutsch</option>
                    <option value="ru">Русский</option>
                  </Select>
                </FormControl>

                <FormControl>
                  <FormLabel fontSize="sm" color={headingColor}>Timezone</FormLabel>
                  <Select
                    value={formData.ui_settings.timezone}
                    onChange={(e) => setFormData(prev => ({ 
                      ...prev, 
                      ui_settings: { ...prev.ui_settings, timezone: e.target.value } 
                    }))}
                    bg={inputBg}
                    borderColor={borderColor}
                  >
                    <option value="UTC">UTC</option>
                    <option value="America/New_York">Eastern Time</option>
                    <option value="America/Chicago">Central Time</option>
                    <option value="America/Denver">Mountain Time</option>
                    <option value="America/Los_Angeles">Pacific Time</option>
                    <option value="Europe/London">London</option>
                    <option value="Europe/Paris">Paris</option>
                    <option value="Europe/Moscow">Moscow</option>
                    <option value="Asia/Tokyo">Tokyo</option>
                  </Select>
                </FormControl>

                <FormControl>
                  <FormLabel fontSize="sm" color={headingColor}>Date format</FormLabel>
                  <Select
                    value={formData.ui_settings.date_format}
                    onChange={(e) => setFormData(prev => ({ 
                      ...prev, 
                      ui_settings: { ...prev.ui_settings, date_format: e.target.value } 
                    }))}
                    bg={inputBg}
                    borderColor={borderColor}
                  >
                    <option value="DD/MM/YYYY">DD/MM/YYYY</option>
                    <option value="MM/DD/YYYY">MM/DD/YYYY</option>
                    <option value="YYYY-MM-DD">YYYY-MM-DD</option>
                    <option value="DD.MM.YYYY">DD.MM.YYYY</option>
                  </Select>
                </FormControl>

                <FormControl>
                  <FormLabel fontSize="sm" color={headingColor}>Time format</FormLabel>
                  <Select
                    value={formData.ui_settings.time_format}
                    onChange={(e) => setFormData(prev => ({ 
                      ...prev, 
                      ui_settings: { ...prev.ui_settings, time_format: e.target.value } 
                    }))}
                    bg={inputBg}
                    borderColor={borderColor}
                  >
                    <option value="24h">24-hour</option>
                    <option value="12h">12-hour</option>
                  </Select>
                </FormControl>
              </SimpleGrid>
            </Box>
          </VStack>
        );

      case 3:
        return (
          <VStack spacing={6} align="stretch">
            <Alert status="info" borderRadius="md" variant="subtle">
              <AlertIcon />
              <Text fontSize="sm" color={textColor}>Configure default settings for team members</Text>
            </Alert>

            <Box bg={surfaceBg} p={4} borderRadius="md" border="1px" borderColor={borderColor}>
              <Text fontWeight="semibold" mb={3} color={headingColor}>Member Settings</Text>
              <VStack spacing={4} align="stretch">
                <FormControl>
                  <FormLabel fontSize="sm" color={headingColor}>Default role name</FormLabel>
                  <Input
                    value={formData.user_settings.default_role_name}
                    onChange={(e) => setFormData(prev => ({ 
                      ...prev, 
                      user_settings: { ...prev.user_settings, default_role_name: e.target.value } 
                    }))}
                    placeholder="member"
                    bg={inputBg}
                    borderColor={borderColor}
                  />
                  <FormHelperText color={mutedTextColor}>Default role for new members</FormHelperText>
                </FormControl>

                <HStack justifyContent="space-between">
                  <VStack align="start" spacing={1}>
                    <Text fontSize="sm" fontWeight="medium" color={headingColor}>Auto-approve members</Text>
                    <Text fontSize="xs" color={mutedTextColor}>Automatically approve membership requests</Text>
                  </VStack>
                  <Switch
                    isChecked={formData.user_settings.auto_approve_members}
                    onChange={(e) => setFormData(prev => ({ 
                      ...prev, 
                      user_settings: { ...prev.user_settings, auto_approve_members: e.target.checked } 
                    }))}
                    colorScheme={primaryColorScheme}
                  />
                </HStack>

                <HStack justifyContent="space-between">
                  <VStack align="start" spacing={1}>
                    <Text fontSize="sm" fontWeight="medium" color={headingColor}>Allow member invitations</Text>
                    <Text fontSize="xs" color={mutedTextColor}>Let members invite others</Text>
                  </VStack>
                  <Switch
                    isChecked={formData.user_settings.allow_member_invitations}
                    onChange={(e) => setFormData(prev => ({ 
                      ...prev, 
                      user_settings: { ...prev.user_settings, allow_member_invitations: e.target.checked } 
                    }))}
                    colorScheme={primaryColorScheme}
                  />
                </HStack>
              </VStack>
            </Box>
          </VStack>
        );

      case 4:
        return (
          <VStack spacing={6} align="stretch">
            <Alert status="info" borderRadius="md" variant="subtle">
              <AlertIcon />
              <Text fontSize="sm" color={textColor}>Configure notification preferences for the team</Text>
            </Alert>

            <Box bg={surfaceBg} p={4} borderRadius="md" border="1px" borderColor={borderColor}>
              <Text fontWeight="semibold" mb={3} color={headingColor}>Notification Settings</Text>
              <VStack spacing={4} align="stretch">
                <HStack justifyContent="space-between">
                  <VStack align="start" spacing={1}>
                    <Text fontSize="sm" fontWeight="medium" color={headingColor}>Enable notifications</Text>
                    <Text fontSize="xs" color={mutedTextColor}>Master switch for all notifications</Text>
                  </VStack>
                  <Switch
                    isChecked={formData.notification_settings.notifications_enabled}
                    onChange={(e) => setFormData(prev => ({ 
                      ...prev, 
                      notification_settings: { ...prev.notification_settings, notifications_enabled: e.target.checked } 
                    }))}
                    colorScheme={primaryColorScheme}
                  />
                </HStack>

                {formData.notification_settings.notifications_enabled && (
                  <>
                    <HStack justifyContent="space-between">
                      <VStack align="start" spacing={1}>
                        <Text fontSize="sm" fontWeight="medium" color={headingColor}>Email notifications</Text>
                        <Text fontSize="xs" color={mutedTextColor}>Send notifications via email</Text>
                      </VStack>
                      <Switch
                        isChecked={formData.notification_settings.email_enabled}
                        onChange={(e) => setFormData(prev => ({ 
                          ...prev, 
                          notification_settings: { ...prev.notification_settings, email_enabled: e.target.checked } 
                        }))}
                        colorScheme={primaryColorScheme}
                      />
                    </HStack>

                    <HStack justifyContent="space-between">
                      <VStack align="start" spacing={1}>
                        <Text fontSize="sm" fontWeight="medium" color={headingColor}>Push notifications</Text>
                        <Text fontSize="xs" color={mutedTextColor}>Send push notifications</Text>
                      </VStack>
                      <Switch
                        isChecked={formData.notification_settings.push_enabled}
                        onChange={(e) => setFormData(prev => ({ 
                          ...prev, 
                          notification_settings: { ...prev.notification_settings, push_enabled: e.target.checked } 
                        }))}
                        colorScheme={primaryColorScheme}
                      />
                    </HStack>
                  </>
                )}
              </VStack>
            </Box>
          </VStack>
        );

      default:
        return null;
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="4xl" scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent bg={modalBg} borderColor={borderColor} maxH="90vh">
        <ModalHeader color={headingColor} pb={2}>
          {t('teams.createTeam')} - {steps[activeStep].title}
        </ModalHeader>
        <Text fontSize="sm" color={mutedTextColor} px={6} pb={4}>
          {steps[activeStep].description}
        </Text>
        <ModalCloseButton color={textColor} />

        <ModalBody bg={modalBg} pt={0}>
          <VStack spacing={6} align="stretch">
            {/* Stepper */}
            <Stepper index={activeStep} colorScheme={primaryColorScheme} size="sm">
              {steps.map((step, index) => (
                <Step key={index}>
                  <StepIndicator>
                    <StepStatus
                      complete={<StepIcon />}
                      incomplete={<StepNumber />}
                      active={<StepNumber />}
                    />
                  </StepIndicator>

                  <Box flexShrink="0" minW="0">
                    <StepTitle fontSize="xs">{step.title}</StepTitle>
                  </Box>

                  <StepSeparator />
                </Step>
              ))}
            </Stepper>

            {/* Step Content */}
            <Box minH="400px">
              {renderStepContent()}
            </Box>
          </VStack>
        </ModalBody>

        <ModalFooter>
          <HStack spacing={4}>
            <Button
              onClick={onClose}
              variant="ghost"
              colorScheme="gray"
            >
              {t('common.cancel')}
            </Button>
            
            {activeStep > 0 && (
              <Button
                onClick={handleBack}
                variant="outline"
                colorScheme={primaryColorScheme}
              >
                {t('common.previous')}
              </Button>
            )}
            
            {activeStep < steps.length - 1 ? (
              <Button
                onClick={handleNext}
                colorScheme={primaryColorScheme}
              >
                {t('common.next')}
              </Button>
            ) : (
              <Button
                onClick={handleSubmit}
                colorScheme={primaryColorScheme}
                isLoading={isSubmitting}
                loadingText={t('teams.creating')}
              >
                {t('teams.createTeam')}
              </Button>
            )}
          </HStack>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}; 